import { useMemo, useState, type CSSProperties } from "react";
import "../styles/product-showcase.css";
import { getModuleLauncherLogoUrl } from "../../branding/module-logo-catalog";
import type { EnrichedProduct } from "../product-catalog";
import {
  partitionProductsByLicense,
} from "../product-catalog";
import { useProductCatalog } from "../hooks/useProductCatalog";
import { resolvePepApiBase } from "../pep-catalog.service";
import { buildPepCatalogUrl, buildPepProductTrialUrl } from "../trial-links";

export interface ProductShowcaseProps {
  licensedModuleIds: string[];
  /** Libellés d'édition par module — ex. { edu: "ITaaS Edu Essential" } */
  moduleEditionLabels?: Record<string, string>;
  pepBaseUrl: string;
  pepApiBaseUrl?: string;
  userEmail?: string;
  organizationName?: string;
  onOpenModule?: (moduleId: string) => void;
  onActivateModule?: (moduleId: string) => Promise<void>;
  activatingModuleId?: string | null;
  enableTenantActivation?: boolean;
  showHero?: boolean;
  utmSource?: string;
}

function ProductCard({
  product,
  variant,
  pepBaseUrl,
  onOpen,
  onActivate,
  activating,
  enableTenantActivation,
  utmSource,
  userEmail,
  organizationName,
  editionLabel,
}: {
  product: EnrichedProduct;
  variant: "active" | "discover";
  pepBaseUrl: string;
  onOpen?: (moduleId: string) => void;
  onActivate?: (moduleId: string) => Promise<void>;
  activating?: boolean;
  enableTenantActivation?: boolean;
  utmSource: string;
  userEmail?: string;
  organizationName?: string;
  editionLabel?: string;
}) {
  const { entry, marketing } = product;
  const logoSrc = useMemo(() => getModuleLauncherLogoUrl(entry.id), [entry.id]);
  const [logoFailed, setLogoFailed] = useState(false);
  const trialUrl = buildPepProductTrialUrl(pepBaseUrl, entry.id, {
    utm_source: utmSource,
    utm_medium: variant === "discover" ? "cross_sell" : "launcher",
    email: userEmail,
    company: organizationName,
  });

  return (
    <article
      className={`itaas-mkt-card ${variant === "active" ? "itaas-mkt-card--active" : ""}`}
      style={{ "--mkt-accent": marketing.accent } as CSSProperties}
    >
      <div className="itaas-mkt-card-head">
        {!logoFailed ? (
          <img
            src={logoSrc}
            alt=""
            className="itaas-mkt-logo"
            loading="lazy"
            decoding="async"
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <div
            className="itaas-mkt-icon"
            style={{ background: marketing.accent }}
            aria-hidden
          >
            <i className={`ti ti-${marketing.icon}`} />
          </div>
        )}
        <div className="itaas-mkt-card-head-text">
          <h3 className="itaas-mkt-card-title">{entry.displayName}</h3>
          {editionLabel ? (
            <span className="itaas-mkt-edition-badge" title={editionLabel}>
              {editionLabel}
            </span>
          ) : null}
          <p className="itaas-mkt-card-tagline">{marketing.tagline}</p>
        </div>
      </div>

      {marketing.benefits.length > 0 && (
        <ul className="itaas-mkt-benefits">
          {marketing.benefits.map((benefit) => (
            <li key={benefit}>{benefit}</li>
          ))}
        </ul>
      )}

      <div className="itaas-mkt-card-actions">
        {variant === "active" && onOpen ? (
          <button
            type="button"
            className="itaas-mkt-btn itaas-mkt-btn--primary itaas-mkt-btn--accent"
            onClick={() => onOpen(entry.id)}
          >
            Ouvrir
            <i className="ti ti-arrow-right" />
          </button>
        ) : (
          <>
            {enableTenantActivation && onActivate ? (
              <button
                type="button"
                className="itaas-mkt-btn itaas-mkt-btn--primary"
                disabled={activating}
                onClick={() => void onActivate(entry.id)}
              >
                {activating ? "Activation…" : "Activer sur mon tenant"}
                <i className="ti ti-plus" />
              </button>
            ) : (
              <a
                href={trialUrl}
                className="itaas-mkt-btn itaas-mkt-btn--primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Essai gratuit
              </a>
            )}
            <a
              href={enableTenantActivation && onActivate ? trialUrl : buildPepCatalogUrl(pepBaseUrl)}
              className="itaas-mkt-btn itaas-mkt-btn--outline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {enableTenantActivation && onActivate ? "Essai PEP" : "En savoir plus"}
            </a>
          </>
        )}
      </div>
    </article>
  );
}

export function ProductShowcase({
  licensedModuleIds,
  moduleEditionLabels,
  pepBaseUrl,
  pepApiBaseUrl,
  userEmail,
  organizationName,
  onOpenModule,
  onActivateModule,
  activatingModuleId,
  enableTenantActivation = false,
  showHero = true,
  utmSource = "itaas",
}: ProductShowcaseProps) {
  const apiBase = useMemo(() => {
    try {
      return pepApiBaseUrl ?? resolvePepApiBase();
    } catch {
      return undefined;
    }
  }, [pepApiBaseUrl]);
  const { loading, source, productCount } = useProductCatalog(apiBase);
  const { active, discover } = partitionProductsByLicense(licensedModuleIds);

  return (
    <div className="itaas-mkt">
      {!loading && source === "pep" && productCount > 0 && (
        <p className="text-muted small mb-3">
          Catalogue synchronisé depuis PEP ({productCount} produit
          {productCount > 1 ? "s" : ""}).
        </p>
      )}

      {showHero && (
        <header className="itaas-mkt-hero">
          <h1>Une plateforme, tous vos métiers</h1>
          <p>
            ITaaS regroupe RH, écoles, hôpitaux, stock, mines et plus encore.
            Commencez avec un module — découvrez les autres quand vous êtes prêt.
          </p>
          <div className="itaas-mkt-hero-badges">
            <span className="itaas-mkt-badge">
              <i className="ti ti-clock" /> Essai 30 jours
            </span>
            <span className="itaas-mkt-badge">
              <i className="ti ti-users" /> Multi-tenant
            </span>
            <span className="itaas-mkt-badge">
              <i className="ti ti-building-warehouse" /> Stock partagé
            </span>
          </div>
          <div className="itaas-mkt-stats">
            <div className="itaas-mkt-stat">
              <strong>{active.length || "—"}</strong>
              <span>module{active.length > 1 ? "s" : ""} actif{active.length > 1 ? "s" : ""}</span>
            </div>
            <div className="itaas-mkt-stat">
              <strong>{discover.length}+</strong>
              <span>produits à découvrir</span>
            </div>
            <div className="itaas-mkt-stat">
              <strong>1</strong>
              <span>compte unique</span>
            </div>
          </div>
        </header>
      )}

      {active.length > 0 && (
        <section className="mb-4">
          <h2 className="itaas-mkt-section-title">Vos modules — accès immédiat</h2>
          <div className="itaas-mkt-grid itaas-mkt-grid--active-modules">
            {active.map((product) => (
              <ProductCard
                key={product.entry.id}
                product={product}
                variant="active"
                pepBaseUrl={pepBaseUrl}
                onOpen={onOpenModule}
                utmSource={utmSource}
                userEmail={userEmail}
                organizationName={organizationName}
                editionLabel={moduleEditionLabels?.[product.entry.id]}
              />
            ))}
          </div>
        </section>
      )}

      {discover.length > 0 && (
        <section>
          <h2 className="itaas-mkt-section-title">
            Découvrez aussi nos autres produits
          </h2>
          <p className="text-muted small mb-3">
            Votre essai inclut les modules choisis. Explorez le reste du catalogue ITaaS
            et ajoutez-les quand vous le souhaitez.
          </p>
          <div className="itaas-mkt-carousel" role="region" aria-label="Autres produits ITaaS">
            {discover.map((product) => (
              <div className="itaas-mkt-carousel__slide" key={product.entry.id}>
                <ProductCard
                  product={product}
                  variant="discover"
                  pepBaseUrl={pepBaseUrl}
                  onActivate={onActivateModule}
                  activating={activatingModuleId === product.entry.id}
                  enableTenantActivation={enableTenantActivation}
                  utmSource={utmSource}
                  userEmail={userEmail}
                  organizationName={organizationName}
                />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default ProductShowcase;
